<template>
  <div class="playbook-view">
    <!-- Header -->
    <div class="flex items-start justify-between mb-3">
      <div>
        <h1 class="text-xl font-semibold text-gray-900">Peer Practice Playbook</h1>
        <p v-if="header.match_summary" class="sensitive text-xs text-gray-500 mt-0.5" data-demo-blur>{{ header.match_summary }}</p>
        <p v-if="practiceContextLine" class="text-sm text-gray-700 mt-2 font-medium">{{ practiceContextLine }}</p>
      </div>
      <button
        class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="regenerating"
        @click="$emit('regenerate')"
      >
        <svg class="w-4 h-4" :class="{ 'animate-spin': regenerating }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        {{ regenerating ? 'Regenerating…' : 'Regenerate Playbook' }}
      </button>
    </div>

    <!-- Summary Banner -->
    <div v-if="header.summary_banner" class="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
      <p class="sensitive text-sm text-emerald-900" data-demo-blur>{{ header.summary_banner }}</p>
    </div>

    <!-- Target Context Strip -->
    <div v-if="practiceContext" class="mb-4 p-3 rounded-lg border border-gray-200 bg-white">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h2 class="sensitive text-sm font-semibold text-gray-900">{{ practiceContext.name }}</h2>
          <div v-if="targetTags.length > 0 || practiceContext.panel_size" class="mt-2 flex items-center gap-1.5 flex-wrap">
            <span
              v-for="tag in targetTags"
              :key="tag"
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700"
            >{{ tagLabel(tag) }}</span>
            <span v-if="practiceContext.panel_size" class="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
              Panel: {{ practiceContext.panel_size.toLocaleString() }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Peers Table -->
    <section v-if="peers.length > 0" class="mb-4 rounded-lg border border-gray-200 bg-white">
      <header class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <button class="flex items-center gap-2" @click="showPeers = !showPeers">
          <svg
            class="w-4 h-4 transition-transform text-gray-400"
            :class="{ 'rotate-90': showPeers }"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span class="w-2 h-2 rounded-full bg-violet-500"></span>
          <h2 class="text-sm font-semibold text-gray-900">Peer Practices</h2>
          <span class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">{{ peers.length }}</span>
        </button>
        <span class="text-xs text-gray-400">Matched on shared structural tags</span>
      </header>
      <div v-if="showPeers" class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 border-b border-gray-100">
            <tr>
              <th class="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide">Practice</th>
              <th class="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide">Panel</th>
              <th class="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide">Shared Tags</th>
              <th class="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wide">Match</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="peer in peers" :key="peer.entity_id" class="hover:bg-gray-50">
              <td class="sensitive px-3 py-2 text-gray-900 font-medium align-top">{{ peer.name }}</td>
              <td class="px-3 py-2 text-gray-600 align-top">{{ peer.panel_size ? peer.panel_size.toLocaleString() : '—' }}</td>
              <td class="px-3 py-2 align-top">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="tag in (peer.shared_tags || [])"
                    :key="tag"
                    class="px-1.5 py-0.5 text-xs rounded-full bg-violet-50 text-violet-700"
                  >{{ tagLabel(tag) }}</span>
                </div>
              </td>
              <td class="px-3 py-2 text-xs text-gray-600 align-top">{{ peer.match_reason || '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Threads -->
    <div v-if="threads.length === 0" class="p-6 text-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg">
      No recurring patterns surfaced across peers on the target's active threads.
    </div>

    <section v-for="thread in threads" :key="thread.thread_id" class="mb-4 rounded-lg border border-gray-200 bg-white">
      <header class="px-4 py-3 border-b border-gray-100">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2 min-w-0">
            <span class="w-2 h-2 rounded-full bg-blue-500 shrink-0"></span>
            <h2 class="text-sm font-semibold text-gray-900 truncate">{{ thread.thread_name }}</h2>
            <span
              v-if="thread.performance_domain && thread.performance_domain !== thread.thread_name"
              class="text-xs text-gray-500 shrink-0"
            >· {{ thread.performance_domain }}</span>
          </div>
          <span class="text-xs text-gray-400 shrink-0">{{ threadPeersTraced(thread) }} peers reviewed</span>
        </div>
        <p
          v-if="thread.description"
          class="sensitive mt-1 text-xs text-gray-500 leading-relaxed line-clamp-2"
          data-demo-blur
          :title="thread.description"
        >{{ thread.description }}</p>
      </header>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x divide-gray-100">

        <!-- Worked column -->
        <div class="p-4">
          <div class="flex items-center gap-2 mb-3">
            <span class="text-emerald-700 text-sm">✓</span>
            <h3 class="text-xs font-semibold text-emerald-900 uppercase tracking-wide">What Worked</h3>
          </div>
          <div v-if="!thread.worked || thread.worked.length === 0" class="text-xs text-gray-400 py-4 text-center">
            No 2-peer patterns surfaced.
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="(item, idx) in thread.worked"
              :key="'w-' + thread.thread_id + '-' + idx"
              class="rounded border border-emerald-100 bg-emerald-50/40"
            >
              <div class="p-3">
                <div class="flex items-start justify-between gap-2">
                  <h4 class="text-sm font-semibold text-gray-900 leading-snug">{{ item.name }}</h4>
                </div>
                <p class="mt-1 text-xs text-gray-600">
                  Said to work at <span class="font-semibold text-gray-800">{{ item.peer_count }}</span> of {{ item.total_peers_traced || thread.total_peers_traced }} peers
                </p>
                <button class="mt-2 text-xs text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline" @click="toggle('w', thread.thread_id, idx)">
                  → {{ isExpanded('w', thread.thread_id, idx) ? 'Hide quotes' : 'View quotes' }}
                </button>
              </div>
              <div v-if="isExpanded('w', thread.thread_id, idx)" class="border-t border-emerald-100 bg-white p-3 space-y-2">
                <div
                  v-for="(q, qi) in (item.evidence || [])"
                  :key="qi"
                  class="border-l-2 border-emerald-400 pl-2"
                >
                  <p class="sensitive text-xs text-gray-700 italic" data-demo-blur>"{{ q.quote }}"</p>
                  <p class="text-[11px] text-gray-500 mt-0.5">— {{ q.peer_name }}<span v-if="q.engagement_date">, {{ formatDate(q.engagement_date) }}</span></p>
                </div>
                <div v-if="item.why_it_works" class="pt-2 border-t border-gray-100">
                  <p class="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Synthesis</p>
                  <p class="sensitive text-xs text-gray-700 mt-0.5" data-demo-blur>{{ item.why_it_works }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Didn't Work column -->
        <div class="p-4">
          <div class="flex items-center gap-2 mb-3">
            <span class="text-red-700 text-sm">×</span>
            <h3 class="text-xs font-semibold text-red-900 uppercase tracking-wide">What Didn't Work</h3>
          </div>
          <div v-if="!thread.didnt_work || thread.didnt_work.length === 0" class="text-xs text-gray-400 py-4 text-center">
            No 2-peer patterns surfaced.
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="(item, idx) in thread.didnt_work"
              :key="'f-' + thread.thread_id + '-' + idx"
              class="rounded border border-red-100 bg-red-50/40"
            >
              <div class="p-3">
                <div class="flex items-start justify-between gap-2">
                  <h4 class="text-sm font-semibold text-gray-900 leading-snug">{{ item.name }}</h4>
                  <span v-if="item.mixed" class="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 shrink-0">Mixed</span>
                </div>
                <p class="mt-1 text-xs text-gray-600">
                  Failed at <span class="font-semibold text-gray-800">{{ item.peer_count }}</span> of {{ item.total_peers_traced || thread.total_peers_traced }} peers
                </p>
                <button class="mt-2 text-xs text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline" @click="toggle('f', thread.thread_id, idx)">
                  → {{ isExpanded('f', thread.thread_id, idx) ? 'Hide quotes' : 'View quotes' }}
                </button>
              </div>
              <div v-if="isExpanded('f', thread.thread_id, idx)" class="border-t border-red-100 bg-white p-3 space-y-2">
                <div
                  v-for="(q, qi) in (item.evidence || [])"
                  :key="qi"
                  class="border-l-2 border-red-400 pl-2"
                >
                  <p class="sensitive text-xs text-gray-700 italic" data-demo-blur>"{{ q.quote }}"</p>
                  <p class="text-[11px] text-gray-500 mt-0.5">— {{ q.peer_name }}<span v-if="q.engagement_date">, {{ formatDate(q.engagement_date) }}</span></p>
                </div>
                <div v-if="item.why_it_failed" class="pt-2 border-t border-gray-100">
                  <p class="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Synthesis</p>
                  <p class="sensitive text-xs text-gray-700 mt-0.5" data-demo-blur>{{ item.why_it_failed }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'PlaybookView',
  props: {
    responseData: { type: Object, required: true },
    regenerating: { type: Boolean, default: false },
  },
  emits: ['regenerate'],
  data() {
    return {
      expanded: {},
      showPeers: false,
    }
  },
  computed: {
    header() {
      return this.responseData.header || {}
    },
    practiceContext() {
      return this.responseData.practice_context || null
    },
    targetTags() {
      return this.practiceContext?.structural_tags || []
    },
    peers() {
      return this.responseData.peers || []
    },
    threads() {
      return this.responseData.threads || []
    },
    practiceContextLine() {
      const parts = []
      if (this.practiceContext?.name) parts.push(this.practiceContext.name)
      if (this.practiceContext?.tenant_name) parts.push(this.practiceContext.tenant_name)
      return parts.join(' · ')
    },
  },
  methods: {
    isExpanded(kind, threadId, idx) {
      return !!this.expanded[`${kind}:${threadId}:${idx}`]
    },
    toggle(kind, threadId, idx) {
      const key = `${kind}:${threadId}:${idx}`
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] }
    },
    formatDate(d) {
      if (!d) return ''
      const date = new Date(d)
      if (isNaN(date)) return d
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    threadPeersTraced(thread) {
      const items = [...(thread.worked || []), ...(thread.didnt_work || [])]
      let max = 0
      for (const m of items) {
        if (typeof m.total_peers_traced === 'number' && m.total_peers_traced > max) {
          max = m.total_peers_traced
        }
      }
      return max
    },
    tagLabel(tag) {
      return String(tag).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
  },
}
</script>
